/*
 * Pantalla "Marcaciones" (ruta /marcaciones, permiso view_attendance): contenedor del tablero.
 *
 * - Lee el estado de la URL (sección, periodo, fechas, zona, tienda, empleado; utils/period.js) y lo
 *   escribe con history.replace (filtros) o history.push (cambio de sección).
 * - Pide las jornadas del rango a GET /attendance/jornadas/ (etapa 1) SIN filtros de zona, tienda o
 *   empleado y filtra en el navegador: así las opciones de los selectores salen de los datos del
 *   periodo y cambiar un filtro no vuelve a llamar al servidor. Horas trabajadas pide /attendance/horas/
 *   con todos los filtros (lo hace la sección).
 * - Los datos NO van a redux (el store se persiste completo en localStorage con redux-persist).
 * - Con el permiso change_attendanceschedule muestra "Configurar horarios" (components/ScheduleModal.js);
 *   con generate_report_attendance, cada sección muestra "Exportar a Excel" (components/SectionHeader.js).
 * Secciones: sections/*.js; todas reciben las mismas props (ver SECTION_COMPONENTS).
 */
import React from "react";
import {useSelector} from "react-redux";
import {useHistory, useLocation} from "react-router-dom";
import {ToastContainer} from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Breadcrumbs from "../../../components/breadcrumbs";
import {fetchWorkdays} from "../../../redux/attendance";
import useRemoteData from "./hooks/useRemoteData";
import {buildParams, periodRange, rangeError, readFilters, todayISO, writeFilters} from "./utils/period";
import {applyEntityFilters, filterOptions} from "./utils/rows";
import SectionMenu from "./components/SectionMenu";
import PeriodFilters from "./components/PeriodFilters";
import ScheduleModal from "./components/ScheduleModal";
import Ingresos from "./sections/Ingresos";
import Almuerzos from "./sections/Almuerzos";
import Salidas from "./sections/Salidas";
import Horas from "./sections/Horas";
import Dashboard from "./sections/Dashboard";
import "./attendance.css";

const SECTION_COMPONENTS = {
    ingresos: Ingresos,
    almuerzos: Almuerzos,
    salidas: Salidas,
    horas: Horas,
    dashboard: Dashboard,
};

const Attendance = (props) => {
    const location = useLocation();
    const history = useHistory();
    const login = useSelector((state) => state.users_module.login.data) || {};
    const token = login.token;
    const permisos = Object.values(props);

    const today = todayISO();
    const filters = readFilters(location.search, today);
    const range = periodRange(filters);
    const invalidRange = rangeError(range);
    const workdayParams = buildParams({}, range);
    const workdays = useRemoteData(
        () => fetchWorkdays(token, workdayParams),
        token && !invalidRange ? JSON.stringify(workdayParams) : null,
    );
    // useRemoteData conserva `data` del periodo anterior mientras carga: solo las filas de una respuesta
    // ya recibida (status 'ok') se presentan como actuales; las opciones de los selectores sí usan lo último.
    const optionRows = workdays.data ? workdays.data.datos : [];
    const rows = workdays.status === 'ok' ? applyEntityFilters(optionRows, filters) : [];

    const setFilters = (next) => history.replace({pathname: location.pathname, search: writeFilters(next)});
    const goToSection = (seccion) => history.push({pathname: location.pathname, search: writeFilters({...filters, seccion})});
    const Section = SECTION_COMPONENTS[filters.seccion] || Ingresos;
    const canEditSchedule = permisos.some((permiso) => permiso && permiso.codigo === 'change_attendanceschedule');
    const canExport = permisos.some((permiso) => permiso && permiso.codigo === 'generate_report_attendance');

    return (
        <div className="app-content content">
            <div className="content-overlay"/>
            <div className="content-wrapper mc-root">
                <div className="content-header row">
                    <div className="content-header-left col-12 mb-1 mt-1 d-flex align-items-center justify-content-between flex-wrap">
                        <Breadcrumbs title="Marcaciones" items={[{label: "Marcaciones", link: "/marcaciones"}]}/>
                        {canEditSchedule && <ScheduleModal token={token}/>}
                    </div>
                </div>
                <div className="content-body">
                    <SectionMenu active={filters.seccion} onSelect={goToSection}/>
                    <PeriodFilters filters={filters} today={today} options={filterOptions(optionRows, filters)}
                                   rangeMessage={invalidRange} onChange={setFilters}/>
                    {invalidRange ? (
                        <div className="mc-state mc-state--error" role="alert">
                            <i className="bx bx-error-circle" aria-hidden="true"/>
                            <p className="mc-state__title">Revisa el rango de fechas</p>
                            <p className="mc-state__text">{invalidRange}</p>
                        </div>
                    ) : (
                        <Section rows={rows} status={workdays.status} message={workdays.message} onRetry={workdays.reload}
                                 filters={filters} range={range} today={today} token={token} canExport={canExport} onFiltersChange={setFilters}
                                 resetKey={writeFilters(filters)}/>
                    )}
                </div>
            </div>
            <ToastContainer/>
        </div>
    );
};

export default Attendance;
